import React from 'react';
import { Card, Descriptions, Typography, Button, Grid, Row, Col } from 'antd';
import { CalendarOutlined, UserOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logEvent } from '../../analytics';
import GenerateDocumentAndRedirectButton from '../patient/GenerateDocumentAndRedirectButton';

const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const NotePatient = ({ patient, onLinkPatient }) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();

  const cardTitle = (
    <>
      <UserOutlined style={{ marginRight: 8 }} />
      {patient ? patient.getFullName() : t('session_details_patient.title')}
    </>
  );

  const cardExtra = (
    patient && (
      <Link
        to={`/patients/${patient.getUUID()}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => logEvent('SessionDetails', 'ViewPatientProfile')}
      >
        {t('session_details_patient.view_profile')}
      </Link>
    )
  );

  if (!patient) {
    return (
      <Card
        size={screens.md ? 'default' : 'small'}
        title={cardTitle}
        style={{ marginBottom: 24 }}
      >
        <Paragraph>{t('session_details_patient.subtitle')}</Paragraph>
        <div>
          <Button type="default" onClick={onLinkPatient}>
            {t('session_details_patient.link')}
          </Button>
        </div>
      </Card>
    );
  }

  const allFields = [
    patient.getDOB() && {
      key: 'dob',
      label: <CalendarOutlined />,
      value: patient.getDOB(),
    },
    patient.getExternalId() && {
      key: 'external_id',
      label: 'ID',
      value: patient.getExternalId(),
    },
    patient.getCompletedSessionsCount() && {
      key: 'sessions',
      label: t('session_details_patient.completed_sessions_count'),
      value: patient.getCompletedSessionsCount(),
    },
    patient.getCompletedCallsCount() && {
      key: 'calls',
      label: t('session_details_patient.completed_calls_count'),
      value: patient.getCompletedCallsCount(),
    },
  ].filter(Boolean);

  const isOneColumn = screens.md || allFields.length <= 3;
  const hasVisitsOrCalls = (patient.getCompletedSessionsCount() || 0) + (patient.getCompletedCallsCount() || 0) > 0;

  return (
    <Card
      size={screens.md ? 'default' : 'small'}
      title={cardTitle}
      extra={cardExtra}
      style={{ marginBottom: 24 }}
    >
      {isOneColumn ? (
        <Descriptions column={1} size="small">
          {allFields.map(({ key, label, value }) => (
            <Descriptions.Item key={key} label={label}>
              <Text>{value}</Text>
            </Descriptions.Item>
          ))}
        </Descriptions>
      ) : (
        <Row gutter={16}>
          <Col span={12}>
            <Descriptions column={1} size="small">
              {allFields
                .slice(0, Math.ceil(allFields.length / 2))
                .map(({ key, label, value }) => (
                  <Descriptions.Item key={key} label={label}>
                    <Text>{value}</Text>
                  </Descriptions.Item>
                ))}
            </Descriptions>
          </Col>
          <Col span={12}>
            <Descriptions column={1} size="small">
              {allFields
                .slice(Math.ceil(allFields.length / 2))
                .map(({ key, label, value }) => (
                  <Descriptions.Item key={key} label={label}>
                    <Text>{value}</Text>
                  </Descriptions.Item>
                ))}
            </Descriptions>
          </Col>
        </Row>
      )}
      {hasVisitsOrCalls && (
        <div style={{ marginTop: 16 }}>
          <GenerateDocumentAndRedirectButton patient={patient} />
        </div>
      )}
    </Card>
  );
};

export default NotePatient;
